import apiClient from "@/api/client";
import type {
  CreateAgreementInput,
  CreateSignatureTypeInput,
  EmployeeBillingPeriod,
  EmployeeFrameworkAgreement,
  EmployeeSignatureBillingAccess,
  EmployeeSignatureType,
  ReplaceSignatureTypeInput,
  SignatoryBrief,
} from "../types";

interface Envelope<T> {
  data: T;
  errors: unknown;
  meta: unknown;
}

export const employeeAgreementsApi = {
  list: (params?: { employee?: string; status?: string }) =>
    apiClient
      .get<Envelope<EmployeeFrameworkAgreement[]>>("/employee-agreements/", { params })
      .then((r) => r.data.data),

  detail: (id: string) =>
    apiClient
      .get<Envelope<EmployeeFrameworkAgreement>>(`/employee-agreements/${id}/`)
      .then((r) => r.data.data),

  create: (payload: CreateAgreementInput) =>
    apiClient
      .post<Envelope<EmployeeFrameworkAgreement>>("/employee-agreements/", payload)
      .then((r) => r.data.data),

  update: (id: string, payload: Partial<CreateAgreementInput>) =>
    apiClient
      .patch<Envelope<EmployeeFrameworkAgreement>>(
        `/employee-agreements/${id}/`,
        payload,
      )
      .then((r) => r.data.data),

  terminate: (id: string) =>
    apiClient
      .post<Envelope<EmployeeFrameworkAgreement>>(
        `/employee-agreements/${id}/terminate/`,
      )
      .then((r) => r.data.data),

  signatories: () =>
    apiClient
      .get<Envelope<SignatoryBrief[]>>("/employee-agreements/signatories/")
      .then((r) => r.data.data),
};

export const signatureTypesApi = {
  list: (params: { agreement: string; include_deleted?: boolean }) =>
    apiClient
      .get<Envelope<EmployeeSignatureType[]>>("/employee-signature-types/", {
        params,
      })
      .then((r) => r.data.data),

  create: (payload: CreateSignatureTypeInput) =>
    apiClient
      .post<Envelope<EmployeeSignatureType>>(
        "/employee-signature-types/",
        payload,
      )
      .then((r) => r.data.data),

  update: (id: string, payload: Partial<CreateSignatureTypeInput>) =>
    apiClient
      .patch<Envelope<EmployeeSignatureType>>(
        `/employee-signature-types/${id}/`,
        payload,
      )
      .then((r) => r.data.data),

  softDelete: (id: string) =>
    apiClient
      .post<Envelope<EmployeeSignatureType>>(
        `/employee-signature-types/${id}/soft-delete/`,
      )
      .then((r) => r.data.data),

  softDeleteAndReplace: (id: string, payload: ReplaceSignatureTypeInput) =>
    apiClient
      .post<Envelope<EmployeeSignatureType>>(
        `/employee-signature-types/${id}/soft-delete-and-replace/`,
        payload,
      )
      .then((r) => r.data.data),
};

export const billingPeriodsApi = {
  list: (params?: { employee?: string }) =>
    apiClient
      .get<Envelope<EmployeeBillingPeriod[]>>(
        "/employee-billing-periods/",
        { params },
      )
      .then((r) => r.data.data),

  detail: (id: string) =>
    apiClient
      .get<Envelope<EmployeeBillingPeriod>>(
        `/employee-billing-periods/${id}/`,
      )
      .then((r) => r.data.data),

  generate: (payload: { agreement_id: string; year: number; month: number }) =>
    apiClient
      .post<Envelope<EmployeeBillingPeriod>>(
        "/employee-billing-periods/generate/",
        payload,
      )
      .then((r) => r.data.data),

  uploadInvoice: (id: string, fileId: string) =>
    apiClient
      .post<Envelope<EmployeeBillingPeriod>>(
        `/employee-billing-periods/${id}/upload-invoice/`,
        { invoice_managed_file: fileId },
      )
      .then((r) => r.data.data),

  markPaid: (id: string) =>
    apiClient
      .post<Envelope<EmployeeBillingPeriod>>(
        `/employee-billing-periods/${id}/mark-paid/`,
      )
      .then((r) => r.data.data),
};

export const billingAccessApi = {
  list: (params?: { director?: string; employee?: string }) =>
    apiClient
      .get<Envelope<EmployeeSignatureBillingAccess[]>>(
        "/employee-signature-billing-access/",
        { params },
      )
      .then((r) => r.data.data),

  create: (director: string, employee: string) =>
    apiClient
      .post<Envelope<EmployeeSignatureBillingAccess>>(
        "/employee-signature-billing-access/",
        { director, employee },
      )
      .then((r) => r.data.data),

  delete: (id: string) =>
    apiClient.delete(`/employee-signature-billing-access/${id}/`),
};
